import { useEffect, useState } from 'react'

import { Box, Flex, Text, keyframes } from '@chakra-ui/react'

const bounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-4px); }
`

const DOT_COUNT = 3
const FADE_MS = 300

interface TypingIndicatorIProps {
  phrase: string
}

/**
 * Presentational component: renders animated bouncing dots alongside a phrase
 * inside a bot-styled chat bubble to indicate the bot is "typing".
 *
 * Each dot bounces with a staggered delay (0s, 0.15s, 0.3s) creating a
 * sequential wave effect on a 1.2s loop. When the phrase prop changes, the
 * text fades out, swaps, and fades back in.
 *
 * Styled to match bot message bubbles in ChatMessage (same bg, border radius).
 */
export const TypingIndicator = ({ phrase }: TypingIndicatorIProps) => {
  const [displayedPhrase, setDisplayedPhrase] = useState(phrase)
  const [isFading, setIsFading] = useState(false)

  useEffect(() => {
    if (phrase === displayedPhrase) return
    setIsFading(true)
    const timer = setTimeout(() => {
      setDisplayedPhrase(phrase)
      setIsFading(false)
    }, FADE_MS)
    return () => clearTimeout(timer)
  }, [phrase])

  return (
    <Flex justify='flex-start' w='100%'>
      <Flex
        align='center'
        bg='bg.100'
        borderBottomRightRadius='lg'
        borderTopLeftRadius='lg'
        borderTopRightRadius='lg'
        gap='10px'
        px={4}
        py={3}
      >
        <Flex align='center' gap='4px'>
          {Array.from({ length: DOT_COUNT }, (_, i) => (
            <Box
              animation={`${bounce} 1.2s ease-in-out ${i * 0.15}s infinite`}
              bg='icon.secondary.light'
              borderRadius='full'
              h='6px'
              key={i}
              w='6px'
            />
          ))}
        </Flex>
        <Text
          color='text.secondary.light'
          fontSize='13px'
          opacity={isFading ? 0 : 1}
          transition={`opacity ${FADE_MS}ms ease`}
          whiteSpace='nowrap'
        >
          {displayedPhrase}...
        </Text>
      </Flex>
    </Flex>
  )
}
